import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// User APIs
export const userAPI = {
  getUsers: (search = '') => api.get(`/users${search ? `?search=${search}` : ''}`),
  getUser: (id) => api.get(`/users/${id}`),
};

// Chat APIs
export const chatAPI = {
  getChats: () => api.get('/chats'),
  getChat: (id) => api.get(`/chats/${id}`),
  createChat: (data) => api.post('/chats', data),
  updateGroup: (id, data) => api.put(`/chats/${id}`, data),
  leaveGroup: (id, newAdminId = null) => 
    api.post(`/chats/${id}/leave${newAdminId ? `?new_admin_id=${newAdminId}` : ''}`),
  deleteChat: (id) => api.delete(`/chats/${id}`),
  clearChat: (id) => api.delete(`/chats/${id}/clear`),
};

// Message APIs
export const messageAPI = {
  getMessages: (chatId, limit = 50, before = null) => 
    api.get(`/chats/${chatId}/messages`, { params: { limit, before } }),
  sendMessage: (data) => api.post('/messages', data),
  editMessage: (id, content) => api.put(`/messages/${id}`, { content }),
  deleteMessage: (id, forEveryone = false) => 
    api.delete(`/messages/${id}?for_everyone=${forEveryone}`),
  addReaction: (id, emoji) => api.post(`/messages/${id}/react`, { emoji }),
  forwardMessage: (id, targetChatId) => 
    api.post(`/messages/${id}/forward`, { target_chat_id: targetChatId }),
  markAsRead: (chatId) => api.post(`/chats/${chatId}/read`),
};

// Upload API
export const uploadAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Push Notification APIs
export const pushAPI = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: () => api.delete('/push/unsubscribe'),
  toggle: (enabled) => api.put(`/push/toggle?enabled=${enabled}`),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (pendingOnly = false) => api.get(`/admin/users?pending_only=${pendingOnly}`),
  approveUser: (id) => api.post(`/admin/users/${id}/approve`),
  rejectUser: (id) => api.post(`/admin/users/${id}/reject`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetPassword: (userId, newPassword) => 
    api.post('/admin/users/reset-password', { user_id: userId, new_password: newPassword }),
};

export default api;
